"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function OurCulinaryJourney() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="236284" id="artistry-vision" className="bg-secondary text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-accent)_0%,transparent_100%)] opacity-30 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={imageRef} className={`lg:col-span-5 relative w-full aspect-[4/5] overflow-hidden rounded-t-[120px] rounded-b-md shadow-xl transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="absolute inset-0 bg-primary/5 z-10" /> {}
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/ivory_lens_photographers.png" alt="Lead photographers of Ivory Lens" className={`w-full h-full object-cover transition-transform duration-[20s] ease-out ${isImageVisible ? "scale-105" : "scale-100"}`} />
            {}
            <div className="absolute top-4 left-4 w-8 h-8 border-t border-l border-accent/40 z-20" />
            <div className="absolute bottom-4 right-4 w-8 h-8 border-b border-r border-accent/40 z-20" />
          </div>

          {}
          <div ref={textRef} className={`lg:col-span-7 flex flex-col justify-center transition-all duration-1000 delay-300 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl text-primary leading-[1.15] mb-10 tracking-tight italic">
              "Capturing the quiet, profound beauty of your unique story."
            </h2>
            
            <div className="space-y-6 text-lg md:text-xl text-foreground/80 font-sans font-light leading-relaxed max-w-2xl">
              <p>
                At Ivory Lens, we believe that wedding photography is an art of observation. Our vision is rooted in refined nostalgia—creating imagery that feels both contemporary and timeless. We focus on the soft interplay of natural light and the genuine emotions that unfold when you think no one is watching.
              </p>
              <p>
                Founded by Elena and Marcus, our studio is dedicated to a soft-toned aesthetic that honors the elegance of your celebration. We don't just take pictures; we curate a visual legacy that allows you to relive the warmth and intimacy of your wedding day for decades to come.
              </p>
              <div className="pt-4">
                <p className="font-default text-3xl text-primary opacity-80">Elena & Marcus</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}